import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useMemo, useState } from 'react'

import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { OLMSApiService } from '@/shared/apis'
import { SearchResult } from '@/shared/components/SearchBar/models/SearchResult'
import { SearchType } from '@/shared/components/SearchBar/models/SearchType'
import { useLocalStorage } from '@/shared/lib/useLocalStorage'
import { useAppSelector } from '@/shared/store'

export type DateRangeMode = 'relative' | 'manual'

export type OrderSearchFilters = {
  mode: DateRangeMode
  pastDays: number
  futureDays: number
  manualFromDate: Dayjs | null
  manualToDate: Dayjs | null
  todaysDate: boolean
}

export const DEFAULT_FILTERS: OrderSearchFilters = {
  mode: 'relative',
  pastDays: 14, // 2 weeks past
  futureDays: 7, // 1 week future
  manualFromDate: null,
  manualToDate: null,
  todaysDate: false,
}

// Constants for period options (moved from DateRangeFilter.tsx)
export const PAST_PERIODS: DropdownOption[] = [
  { label: 'Today', value: 0 },
  { label: '1 week', value: 7 },
  { label: '2 weeks', value: 14 },
  { label: '1 month', value: 30 },
  { label: '2 months', value: 60 },
  { label: '3 months', value: 90 },
]

export const FUTURE_PERIODS: DropdownOption[] = [
  { label: 'Today', value: 0 },
  { label: '1 week', value: 7 },
  { label: '2 weeks', value: 14 },
  { label: '1 month', value: 30 },
]

const validateStoredFilters = (storedFilters: OrderSearchFilters): OrderSearchFilters => {
  const today = dayjs().startOf('day')

  // For manual mode, check if stored dates are valid
  if (storedFilters.mode === 'manual' && storedFilters.manualFromDate) {
    const fromDate = dayjs(storedFilters.manualFromDate)

    if (fromDate.isAfter(today)) {
      return DEFAULT_FILTERS
    }
  }

  // Convert the string ISO string to dayjs object before returning the stored value
  return {
    ...storedFilters,
    manualFromDate: storedFilters.manualFromDate ? dayjs(storedFilters.manualFromDate) : null,
    manualToDate: storedFilters.manualToDate ? dayjs(storedFilters.manualToDate) : null,
  }
}

export const useOrderSearchData = () => {
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const [filters, setFilters] = useLocalStorage<OrderSearchFilters>('orderSearchFilters', DEFAULT_FILTERS, validateStoredFilters)
  const currentScreen = useAppSelector(state => state.userInterface.currentScreen)

  const getCalculatedDateRange = useCallback(() => {
    const today = dayjs().startOf('day')

    if (filters.todaysDate) {
      return {
        from: today,
        to: today,
      }
    }

    if (filters.mode === 'relative') {
      return {
        from: today.subtract(filters.pastDays, 'days'),
        to: today.add(filters.futureDays, 'days'),
      }
    }

    return {
      from: filters.manualFromDate,
      to: filters.manualToDate,
    }
  }, [filters])

  const searchOrders = useCallback(async (
    query: string,
    searchType: SearchType,
  ) => {
    setLoading(true)

    try {
      const dateRange = getCalculatedDateRange()

      const payload = {
        orderNumber: searchType === 'orderNumber' ? query : null,
        orderRef: searchType === 'orderRef' ? query : null,
        orderItemRef: searchType === 'orderItemRef' ? query : null,
        customerName: searchType === 'customerName' ? query : null,
        customerNumber: searchType === 'customerNumber' ? query : null,
        consignmentNumber: searchType === 'consignmentNumber' ? query : null,
        tripNumber: searchType === 'tripNumber' ? query : null,
        street: searchType === 'street' ? query : null,
        suburb: searchType === 'suburb' ? query : null,
        description: searchType === 'description' ? query : null,
        from: dateRange.from?.toISOString() || null,
        to: dateRange.to?.toISOString() || null,
        todaysDate: filters.todaysDate,
      }

      const endpoint = currentScreen === 'planning' ? '/planning/searchOrder' : '/runsheet/searchOrder'
      const response = await OLMSApiService.post<SearchResult[]>(
        endpoint,
        payload,
      )

      setResults(response || [])

      return response
    } catch (error) {
      console.error('Search error:', error)
      setResults([])
      throw error
    } finally {
      setLoading(false)
    }
  }, [filters, currentScreen, getCalculatedDateRange])

  const hasActiveFilters = useMemo(() => {
    return filters.todaysDate ||
      filters.mode !== DEFAULT_FILTERS.mode ||
      filters.pastDays !== DEFAULT_FILTERS.pastDays ||
      filters.futureDays !== DEFAULT_FILTERS.futureDays ||
      (filters.mode === 'manual' && (filters.manualFromDate !== null || filters.manualToDate !== null))
  }, [filters])

  return {
    loading,
    results,
    filters,
    setFilters,
    searchOrders,
    hasActiveFilters,
  }
}
